import React, { useEffect, useRef, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import fontStyles from './font-styles';
import { createPortal } from 'react-dom';
const ANIMATION_DURATION = '200ms';
const ANIMATION_EASING = 'cubic-bezier(0.0, 0.0, 0.2, 1)';
const Overlay = styled.div `
  position: fixed;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(67, 90, 111, 0.699);
`;
const openAnimation = keyframes `
  from {
    transform: scale(0.8);
    opacity: 0;
  }
  to {
    transform: scale(1);
    opacity: 1;
  }
`;
const Root = styled.section `
  ${fontStyles};
  display: flex;
  flex-direction: column;
  padding: 8px 0;
  max-width: calc(100vw - 16px);
  max-height: calc(100vh - 16px);
  width: ${(props) => props.width};
  margin: 8px;
  background: #fff;
  border-radius: 8px;
  animation: ${openAnimation} ${ANIMATION_DURATION} ${ANIMATION_EASING} both;
`;
const Form = styled.form `
  margin-top: -20px;
  display: flex;
  flex-direction: column;
  min-height: 0;
  padding: 5px 10px;
`;
const Header = styled.div `
  flex: 1 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 24px;
  border-bottom: 0;
`;
const Title = styled.h2 `
  position: relative;
  margin: 0;
  color: #031b4a;
  font-size: 18px;
  font-weight: 700;
  line-height: 24px;
`;
const HeaderCancelButton = styled.button `
  padding: 8px 0;
  border: none;
  background: none;
  color: inherit;
  font: inherit;
  font-size: 12px;
  font-weight: 900;
  line-height: 1;
  cursor: pointer;
  color: #031b4a;
`;
const Content = styled.div `
  -ms-overflow-style: none;
  overflow-y: auto;
  padding: 20px 16px 16px;
  padding-bottom: 0;
  min-height: 0;
  font-size: 12px;
  line-height: 1.2;
  p {
    margin: 0;
    &:not(:last-child) {
      margin-bottom: 0.7em;
    }
  }
  a {
    color: #47b881;
    &:hover {
      color: #64c395;
    }
    &:active {
      color: #248953;
    }
  }
`;
const Buttons = styled.div `
  padding: 16px;
  text-align: right;
`;
const Dialog = ({ onCancel = undefined, onSubmit, title, innerRef, children, buttons, width = '960px', }) => {
    const [container, setContainer] = useState();
    const rootRef = useRef(null);
    let form;
    useEffect(() => {
        setContainer(document.createElement('div'));
    }, []);
    useEffect(() => {
        if (!container)
            return;
        container.setAttribute('data-consent-manager-dialog', '');
        document.body.appendChild(container);
    }, [container]);
    useEffect(() => {
        if (!container)
            return;
        if (form) {
            const input = form.querySelector('input,button');
            if (input)
                input.focus();
        }
        document.body.addEventListener('keydown', handleEsc, false);
        document.body.style.overflow = 'hidden';
        innerRef(container);
        return () => {
            document.body.removeEventListener('keydown', handleEsc, false);
            document.body.style.overflow = '';
            container && document.body.removeChild(container);
            setContainer(undefined);
            innerRef(null);
        };
    }, [container]);
    const handleFormRef = (node) => {
        form = node;
    };
    const handleOverlayClick = (e) => {
        // Ignore propogated clicks from inside the dialog
        if (onCancel && rootRef && !rootRef.current?.contains(e.target)) {
            onCancel();
        }
    };
    const handleEsc = (e) => {
        // Esc key
        if (onCancel && e.keyCode === 27) {
            onCancel();
        }
    };
    const dialog = (React.createElement(Overlay, { onClick: handleOverlayClick },
        React.createElement(Root, { ref: rootRef, role: "dialog", "aria-modal": true, width: width },
            React.createElement(Header, null,
                React.createElement(Title, null, title),
                onCancel && (React.createElement(HeaderCancelButton, { onClick: onCancel, title: "Cancel", "aria-label": "Cancel" }, "\u2715"))),
            React.createElement(Form, { ref: handleFormRef, onSubmit: onSubmit },
                React.createElement(Content, null, children),
                React.createElement(Buttons, null, buttons)))));
    return container ? createPortal(dialog, container) : null;
};
export default Dialog;
//# sourceMappingURL=data:application/json;base64,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